import { FC, useCallback, useMemo } from 'react';
import { Badge, Descriptions, Flex } from 'antd';
import {
  ActionButton,
  CustomSegmented,
  DetailLayout,
  getDatePeriodString,
  getUserName,
  isLocked,
  LockedBy,
  ObjectHeader,
  ObjectType,
  Padding,
  Stack,
  TotalEntries,
  useCheckLockAndNavigate,
  UserFormDataType,
  useShowLockData,
  useUserQuery,
  PopconfirmDelete,
  useUserMutation,
  useMutationErrors,
  GetApiErrorMessage,
  useUserInfo,
  cleanArray,
  useAlertContext,
  InfoIconUrl,
  ErrorAlertWithDrawer,
  useQueryParams,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useParams } from 'react-router-dom';
import { useHasPermissionToEdit } from './useHasPermissionToEdit';
import translations from './UserDetails.translations.json';
import styled from 'styled-components';
import { UserViewPermissionsTable } from './UserViewPermissionsTable';
import { UserRoleViewTable } from './UserRoleViewTable';
import { UserBreadcrumbs } from './UserBreadcrumbs';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';

const { addTranslations, useTranslation, t } = i18nNsFactory('user-details');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

export const UserDetails: FC = () => {
  const params = useParams<{ id: string }>();
  const userId = Number(params.id);

  if (Number.isNaN(userId)) {
    throw Error(`Expecting a numeric User ID, but got: ${params.id}`);
  }

  const { data: user } = useUserQuery({ id: userId });

  if (!user) {
    return null;
  }

  return <UserDetailsInner user={user} />;
};

const UserDetailsInner: FC<{ user: UserFormDataType }> = (props) => {
  const { user } = props;
  const [{ segment: segmentParam }, setQueryParams] = useQueryParams<{
    segment: 'roles' | 'viewPermissions';
  }>();
  const segment = segmentParam === 'viewPermissions' ? segmentParam : 'roles';
  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.User,
    user.id,
  );
  const { t } = useTranslation();
  const { mutate } = useUserMutation();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const navigateToUserEdit = useNavigateTo(path.userEdit, { id: user.id });

  const navigateToUserDirectory = useNavigateTo(path.userDirectory);

  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    () => navigateToUserEdit({ segment }),
  );
  const { setSuccess } = useAlertContext();

  const handleDelete = useCallback(() => {
    clearMutationErrors();
    mutate(
      { id: user.id },
      {
        onSuccess: () => {
          navigateToUserDirectory(undefined, { state: { noBlocker: true } });
          setSuccess(t('alert.success.deleted'));
        },
        onError: ({ response }) => {
          setMutationErrors(response?.data.errors || []);
        },
      },
    );
  }, [
    clearMutationErrors,
    mutate,
    user.id,
    navigateToUserDirectory,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const segmentOptions = useMemo(
    () => [
      {
        value: 'roles' as const,
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.roles')} <Badge count={user.roles.length} />
          </Flex>
        ),
      },
      {
        value: 'viewPermissions' as const,
        label: (
          <Flex gap="small" align="center">
            {t('segment-option.view-permissions')}{' '}
            <Badge count={user.viewPermissions.length} />
          </Flex>
        ),
      },
    ],
    [t, user],
  );

  const { user: sessionUser } = useUserInfo();
  const hasPermissionToEdit = useHasPermissionToEdit(user);
  const hasPermissionToDelete = useMemo(
    () => hasPermissionToEdit && sessionUser.id !== user.id,
    [hasPermissionToEdit, user, sessionUser],
  );

  const dropdownItems = useMemo(
    () =>
      hasPermissionToDelete
        ? [
            {
              key: 'delete',
              label: (
                <PopconfirmDelete
                  stopClickPropagation
                  onDelete={handleDelete}
                  genitiveModelName={t('delete.genitive')}
                >
                  {t('button.delete')}
                </PopconfirmDelete>
              ),
            },
          ]
        : [],
    [handleDelete, hasPermissionToDelete, t],
  );

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.USER_DETAILS}
      type="detail"
      defaultWidth={450}
      headerConfig={{
        actions: (
          <>
            <UiLink id="mainAction-link" path={path.userDirectory}>
              {({ type, ...rest }) => (
                <ActionButton layoutType="detail" type="abort" {...rest} />
              )}
            </UiLink>
            {hasPermissionToEdit && (
              <UiLink
                id="mainAction-link"
                path={path.userEdit}
                params={{ id: user.id, segment }}
                onClick={(event) => {
                  if (!event.metaKey && !event.ctrlKey) {
                    checkLockAndNavigateToEdit();
                  }
                }}
              >
                {({ type, ...rest }) => (
                  <ActionButton
                    disabled={isLocked(lockData)}
                    layoutType="detail"
                    type="mainAction"
                    {...rest}
                  />
                )}
              </UiLink>
            )}
          </>
        ),
        dropdownMenu: hasPermissionToDelete
          ? { items: dropdownItems }
          : undefined,
      }}
      breadCrumb={<UserBreadcrumbs data={{ user }} />}
      side={{
        header: (
          <Padding>
            <ObjectHeader
              upperText={user.username}
              lowerText={getUserName(user)}
            />
          </Padding>
        ),
        content: (
          <Padding>
            <Descriptions
              column={1}
              items={cleanArray([
                {
                  key: 'nationalIdNumber',
                  label: t('nationalIdNumber.label'),
                  children: user.nationalIdNumber,
                },
                !user.isSuperadmin && {
                  key: 'validity',
                  label: t('validity.label'),
                  children: getDatePeriodString(
                    user.validPeriod[0],
                    user.validPeriod[1],
                  ),
                },
                {
                  key: 'admin',
                  label: t('admin.label'),
                  children: t(`admin.${user.isAdmin}`),
                },
                {
                  key: 'admin',
                  label: t('superadmin.label'),
                  children: t(`superadmin.${user.isSuperadmin}`),
                },
              ])}
            />
          </Padding>
        ),
        footer: (
          <LockedBy id={user.id} entity={user} objectType={ObjectType.User} />
        ),
      }}
      footerContent={<TotalEntries count={user[segment].length} />}
      alert={<ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />}
    >
      <Padding>
        <ContentWrapper>
          <CustomSegmented<'roles' | 'viewPermissions'>
            value={segment}
            onChange={(segment) => setQueryParams({ segment })}
            options={segmentOptions}
          />
          <RoleTableWrapper $visible={segment === 'roles'}>
            <UserRoleViewTable userRoles={user.roles} />
          </RoleTableWrapper>
          <RoleTableWrapper $visible={segment === 'viewPermissions'}>
            <UserViewPermissionsTable
              userViewPermissions={user.viewPermissions}
            />
          </RoleTableWrapper>
        </ContentWrapper>
      </Padding>
    </DetailLayout>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 100em;
  height: 100%;
  align-self: center;
  align-items: center;
  margin: 0 auto;
`;

const RoleTableWrapper = styled.div<{ $visible?: boolean }>`
  display: ${({ $visible = true }) => ($visible ? 'block' : 'none')};
  width: 100%;
`;
